import { useEffect, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { NameField, SftpFields, WebhookFields, type FieldsProps } from './DestinationFields';
import { ContentCentralFields, NucleusOneFields } from './DestinationProductFields';
import { DestinationIcon } from './DestinationRow';
import { DestinationSheetFooter } from './DestinationSheetFooter';
import { StorageFields, type StorageControls } from './StorageFields';
import { isStorageKind, kindMeta, storageProducts } from './destination-kinds';
import type {
  PaigeDestination,
  PaigeDestinationKind,
  PaigeDestinationsBridge,
} from './paige.global';
import { useDestinationForm } from './use-destination-form';

export interface DestinationSheetProps {
  kind: PaigeDestinationKind;
  existing?: PaigeDestination;
  bridge: PaigeDestinationsBridge;
  onClose: () => void;
  /** After a save; a save hands the destination as Paige stored it. */
  onSaved: (saved?: PaigeDestination) => void;
  /** Remove: the card closes the sheet and asks (RemoveDestinationDialog). */
  onRemove: (existing: PaigeDestination) => void;
}

/**
 * The sheet that adds or edits a destination (webhook, SFTP, Content Central, Nucleus One, a storage
 * destination such as Microsoft 365), over the workbench in CapturePoint's surface language (the
 * same sheet as IntakeSheet). Saves through Paige's bridge; a refused save shows Paige's reason under
 * the form. Secrets are never shown back. A Content Central or Nucleus One destination is added with
 * its key, and the sheet stays open on the stored destination to choose its catalog or project
 * through Paige's pickers ("Check connection" still runs the test); on a storage destination, "Test delivery" saves what the sheet
 * shows (the folder chosen on screen included), then writes and removes a
 * file in its folder.
 */
export function DestinationSheet(props: DestinationSheetProps): ReactNode {
  const { kind, existing, bridge, onClose, onRemove } = props;
  const form = useDestinationForm(props);
  useEscape(onClose);
  return (
    <div className="paige-intake-backdrop" onClick={onClose}>
      <form
        className="paige-intake-sheet paige-destination-form"
        role="dialog"
        aria-modal="true"
        aria-labelledby="paige-destination-title"
        onClick={(event) => event.stopPropagation()}
        onSubmit={(event) => void form.submit(event)}
      >
        <button type="button" className="paige-intake-close" onClick={onClose} aria-label="Close">
          <X size={16} />
        </button>
        <SheetHead kind={kind} existing={existing} />
        <NameField {...form.fields} />
        <KindFields kind={kind} bridge={bridge} storage={form.storage} {...form.fields} />
        <p className="paige-destination-error" role="alert">
          {form.error ?? form.note}
        </p>
        <DestinationSheetFooter
          busy={form.busy}
          existing={Boolean(existing)}
          check={checkLabel(kind, Boolean(existing))}
          submit={submitLabel(kind, Boolean(existing))}
          onCancel={onClose}
          onRemove={() => existing && onRemove(existing)}
          onCheck={form.check}
        />
      </form>
    </div>
  );
}

function checkLabel(kind: PaigeDestinationKind, existing: boolean): string | null {
  if (!existing) return null;
  if (isStorageKind(kind)) return 'Test delivery';
  return kind === 'content-central' || kind === 'nucleus-one' ? 'Check connection' : null;
}

function submitLabel(kind: PaigeDestinationKind, existing: boolean): string {
  if (existing) return 'Save';
  return isStorageKind(kind) ? 'Add and connect' : 'Add';
}

function SheetHead({
  kind,
  existing,
}: {
  kind: PaigeDestinationKind;
  existing?: PaigeDestination;
}) {
  const { noun, line } = kindMeta[kind];
  return (
    <header className="paige-intake-head">
      <DestinationIcon kind={kind} size={26} />
      <h2 id="paige-destination-title">{existing ? `Edit the ${noun}` : `Add a ${noun}`}</h2>
      <p>{line}</p>
    </header>
  );
}

/** The form fields of one kind. */
function KindFields({
  kind,
  bridge,
  storage,
  ...fields
}: FieldsProps & {
  kind: PaigeDestinationKind;
  bridge: PaigeDestinationsBridge;
  storage: StorageControls;
}) {
  if (isStorageKind(kind))
    return (
      <StorageFields
        {...fields}
        kind={kind}
        {...storageProducts[kind]}
        bridge={bridge}
        storage={storage}
      />
    );
  switch (kind) {
    case 'webhook':
      return <WebhookFields {...fields} />;
    case 'sftp':
      return <SftpFields {...fields} />;
    case 'content-central':
      return <ContentCentralFields {...fields} bridge={bridge} />;
    case 'nucleus-one':
      return <NucleusOneFields {...fields} bridge={bridge} />;
  }
}

/** Escape closes the sheet, as in IntakeSheet. */
function useEscape(onClose: () => void) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
}
